import React, { Fragment, useState, useCallback, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import { FC } from "react";
import ClearDataButton from "./ClearDataButton";
import ButtonSubmit from "components/ButtonSubmitWidget";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import NcInputNumber from "components/NcInputNumber/NcInputNumber";
import { useClient } from "ClientContext";
import { useTranslation } from "react-i18next";

export interface GuestsInputProps {
  fieldClassName?: string;
  className?: string;
  buttonSubmitHref?: string;
  hasButtonSubmit?: boolean;
  onChange?: (value: any) => void;
  onSearch?: (value: any) => void;
}

const GuestsInput: FC<GuestsInputProps> = ({
  fieldClassName = "[ nc-hero-field-padding ]",
  className = "[ nc-flex-1 ]",
  buttonSubmitHref = "/listing-stay-map",
  hasButtonSubmit = true,
  onSearch,
  onChange,
}) => {
  const { t } = useTranslation();
  const { global_searh, setGlobal_search, global_max_guests } = useClient();
  
  // Grab the guestsCount from your global search (default to 2 if empty)
  const globalGuestCount = global_searh?.params?.guestsCount;
  const defaultGuestCount = useMemo(
    () => globalGuestCount ?? 2,
    [globalGuestCount]
  );

  const [guestInputValue, setGuestInputValue] = useState(defaultGuestCount);

  // Fallback to 25 if global_max_guests <= 1
  const maxGuests = useMemo(() => {
    return global_max_guests > 1 ? global_max_guests : 25;
  }, [global_max_guests]);

  // Label to display below the numeric value
  const guestLabel = useMemo(() => {
    return guestInputValue ? t("Guests") : t("Add guests");
  }, [guestInputValue, t]);

  /**
   * Whenever the numeric input changes, update local + global states.
   */
  const handleChangeData = useCallback(
    (value: number) => {
      setGuestInputValue(value);
      onChange?.({ guests: value });

      setGlobal_search((prevData) => ({
        ...prevData,
        isSearching: false,
        params: {
          ...prevData.params,
          guestsCount: value,
        },
      }));
    },
    [onChange, setGlobal_search]
  );

  /**
   * Clear button logic – now sets to 1 instead of 0,
   * ensuring we never drop below 1 guest.
   */
  const handleClear = useCallback(() => {
    setGuestInputValue(1);
    setGlobal_search((prevData) => ({
      ...prevData,
      isSearching: false,
      params: {
        ...prevData.params,
        guestsCount: 1,
      },
    }));
  }, [setGlobal_search]);

  // If something is off, optionally return null or fallback
  if (!maxGuests) return null;

  return (
    <Popover className={`flex relative ${className}`}>
      {({ open }) => (
        <>
          <div
            className={`flex-1 z-10 flex items-center focus:outline-none ${
              open ? "nc-hero-field-focused" : ""
            }`}
          >
            <Popover.Button
              className={`relative z-10 flex-1 flex text-left items-center ${fieldClassName} space-x-3 focus:outline-none`}
              onClickCapture={() => document.querySelector("html")?.click()}
            >
              <div className="text-neutral-300 dark:text-neutral-400">
                <UserPlusIcon className="w-5 h-5 lg:w-7 lg:h-7" />
              </div>

              <div className="flex-grow">
                <span className="block xl:text-lg font-semibold">
                  {guestInputValue || ""} {t("Guests")}
                </span>
                <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
                  {guestLabel}
                </span>
              </div>

              {/* Show the ClearDataButton if there's a current value and popover is open */}
              {!!guestInputValue && open && (
                <ClearDataButton onClick={handleClear} />
              )}
            </Popover.Button>

            {/* Optional "search" button */}
            {hasButtonSubmit && (
              <div className="pr-2 xl:pr-4">
                <ButtonSubmit href={buttonSubmitHref} onSearch={onSearch} />
              </div>
            )}
          </div>

          {/* Light overlay behind the popover */}
          {open && (
            <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1 bg-white dark:bg-neutral-800" />
          )}

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-10 w-full sm:min-w-[340px] max-w-sm bg-white dark:bg-neutral-800 top-full mt-3 py-5 sm:py-6 px-4 sm:px-8 rounded-3xl shadow-xl">
              <NcInputNumber
                className="w-full"
                defaultValue={guestInputValue}
                onChange={handleChangeData}
                max={maxGuests}
                // MOST IMPORTANT: min=1 to disallow going below 1
                min={1}
                label={t("Guests")}
                desc={t("Total number of guests")}
              />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default GuestsInput;